import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, TextField, Button, IconButton,
  Autocomplete, InputAdornment, Drawer, Divider, CircularProgress, Alert,
  Checkbox, FormControlLabel, FormControl, InputLabel, Select, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions, Radio, RadioGroup
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import PaymentIcon from '@mui/icons-material/Payment';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { apiFetch } from '../api';
import { useAuth } from './AuthContext';

const PrintHeader = ({ tenant, branch, customer, startDate, endDate }) => (
  <Box className="print-only" sx={{ mb: 3, textAlign: 'center', borderBottom: '2px solid black', pb: 2 }}>
    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{tenant?.company_name}</Typography>
    <Typography variant="body1">{branch?.name && `ब्रांच: ${branch.name}`}</Typography>
    <Typography variant="body2">{tenant?.address}</Typography>
    <Divider sx={{ my: 1, borderColor: 'black' }} />
    <Typography variant="h5" sx={{ fontWeight: 'bold', my: 1 }}>खाते उतारा (Account Statement)</Typography>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, px: 2, textAlign: 'left' }}>
      <Box>
        <Typography><strong>ग्राहक:</strong> {customer?.name}</Typography>
        <Typography><strong>मोबाईल:</strong> {customer?.mobile}</Typography>
      </Box>
      <Box sx={{ textAlign: 'right' }}>
        <Typography><strong>दिनांक:</strong> {new Date().toLocaleDateString('en-GB')}</Typography>
        <Typography><strong>कालावधी:</strong> {startDate} ते {endDate}</Typography>
      </Box>
    </Box>
  </Box>
);

const getTodayDate = () => new Date().toISOString().split('T')[0];

const calculateDays = (issueDateStr, returnDateStr) => {
  if (!issueDateStr || !returnDateStr) return 1;
  const issueDate = new Date(issueDateStr);
  const returnDate = new Date(returnDateStr);
  const diffTime = returnDate.getTime() - issueDate.getTime();
  // +1: issue day आणि return day दोन्ही मोजायचे (inclusive)
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  return diffDays > 0 ? diffDays : 1;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '---';
  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}/${year}`;
};

// IMAGE_BASE_URL — env मधिल port वापरतो (लोकल dev: 8000, production: no port)
const _apiProtocol = process.env.REACT_APP_API_PROTOCOL || 'http';
const _apiPort = process.env.REACT_APP_API_PORT ? `:${process.env.REACT_APP_API_PORT}` : '';
const IMAGE_BASE_URL = `${_apiProtocol}://${window.location.hostname}${_apiPort}/storage`;

const CustomerTransaction = () => {
  const { activeBranch, userType, tenant } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);

  const [customer, setCustomer] = useState(null);
  const [ledger, setLedger] = useState([]);

  const [loading, setLoading] = useState(false);
  const [siteLocation, setSiteLocation] = useState('');
  const [receiptNo, setReceiptNo] = useState('');
  const [vehicleNo, setVehicleNo] = useState('');
  const [reference, setReference] = useState('');
  const [error, setError] = useState('');

  // Drawer States
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('issue');

  // Edit Drawer States (Reused for our unified smart drawer edit mode)
  const [isEditMode, setIsEditMode] = useState(false);
  const [editChallanId, setEditChallanId] = useState(null);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [editChallan, setEditChallan] = useState(null);
  const [editForm, setEditForm] = useState({ date: '', site_location: '', receipt_no: '', vehicle_no: '', reference: '', notes: '' });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');

  // कच्चे बिल Dialog States
  const [billDialogOpen, setBillDialogOpen] = useState(false);
  const [bill2DialogOpen, setBill2DialogOpen] = useState(false);
  const [billAsOfDate, setBillAsOfDate] = useState(getTodayDate());
  const [billItems, setBillItems] = useState([]);
  const [billTotal, setBillTotal] = useState(0);          // pending rent only
  const [billCustomerBalance, setBillCustomerBalance] = useState(0); // existing balance
  const [billGrandTotal, setBillGrandTotal] = useState(0);            // rent + balance
  const [billLoading, setBillLoading] = useState(false);

  // Form States
  const [transactionDate, setTransactionDate] = useState(getTodayDate());
  const [cartItems, setCartItems] = useState([{ material: null, qty: '' }]);
  const [returnItems, setReturnItems] = useState([]); // Array but functionally holds 1 item for specific returns
  const [otherCharges, setOtherCharges] = useState([]);
  const [paymentData, setPaymentData] = useState({
    cash: '',
    online: '',
    remark: '',
    bank_account_id: ''
  });
  const [paymentType, setPaymentType] = useState('credit');
  const [paymentOtherCharges, setPaymentOtherCharges] = useState([{ name: '', amount: '' }]);
  const [sendWhatsApp, setSendWhatsApp] = useState(true);
  const [whatsappShareData, setWhatsappShareData] = useState(null);

  const [startDate, setStartDate] = useState(() => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const startYear = today.getMonth() < 3 ? currentYear - 1 : currentYear;
    return `${startYear}-04-01`;
  });
  const [endDate, setEndDate] = useState(getTodayDate());

  // POS States
  const [posOpen, setPosOpen] = useState(false);
  const [posActiveTab, setPosActiveTab] = useState('issue');
  const [posSearchQuery, setPosSearchQuery] = useState('');
  const [posCategoryFilter, setPosCategoryFilter] = useState('all');
  const [posPendingBillItems, setPosPendingBillItems] = useState([]);
  const [posPendingLoading, setPosPendingLoading] = useState(false);
  const [posSelectedChallanId, setPosSelectedChallanId] = useState(null);

  const fetchPosPendingItems = async (customerId) => {
    if (!customerId) return;
    setPosPendingLoading(true);
    try {
      const today = getTodayDate();
      const res = await apiFetch(`/api/challans/pending-bill?customer_id=${customerId}&as_of_date=${today}`);
      if (res.ok) {
        const data = await res.json();
        setPosPendingBillItems(data.items || []);
      }
    } catch (err) {
      console.error('Error fetching POS pending items:', err);
    } finally {
      setPosPendingLoading(false);
    }
  };

  const handlePosAddToCart = (material) => {
    if (!material) return;
    const existingIdx = cartItems.findIndex(item => item.material && item.material.id === material.id);
    if (existingIdx > -1) {
      const updated = [...cartItems];
      const currentQty = parseFloat(updated[existingIdx].qty) || 0;
      updated[existingIdx].qty = currentQty + 1;
      setCartItems(updated);
    } else {
      if (cartItems.length === 1 && !cartItems[0].material) {
        setCartItems([{ material: material, qty: 1 }]);
      } else {
        setCartItems([...cartItems, { material: material, qty: 1 }]);
      }
    }
  };

  const handlePosUpdateQty = (idx, change) => {
    const updated = [...cartItems];
    const currentQty = parseFloat(updated[idx].qty) || 0;
    const newQty = currentQty + change;
    if (newQty <= 0) {
      setCartItems(cartItems.filter((_, i) => i !== idx));
    } else {
      updated[idx].qty = newQty;
      setCartItems(updated);
    }
  };

  const handlePosChangeQtyDirect = (idx, val) => {
    const updated = [...cartItems];
    updated[idx].qty = val;
    setCartItems(updated);
  };

  const handlePosDeleteCartItem = (idx) => {
    setCartItems(cartItems.filter((_, i) => i !== idx));
  };

  const handlePosClearCart = () => {
    if (window.confirm("तुम्हाला नक्की कार्ट रिकामी करायची आहे का?")) {
      setCartItems([{ material: null, qty: '' }]);
    }
  };

  const handleSelectChallanForReturn = (challan) => {
    setPosSelectedChallanId(challan.challan_id);
    const items = challan.items.map(item => {
      const rent = parseFloat(item.rate) || 0;
      const days = calculateDays(item.issue_date, transactionDate);
      return {
        issue_transaction_id: item.issue_transaction_id,
        issueDate: item.issue_date,
        material_id: item.material_id,
        material_name: item.material_name,
        pending: item.pending_qty,
        returnQty: item.pending_qty, // By default pre-fill with full pending quantity
        days: days,
        rent: rent,
        amount: item.pending_qty * rent * days
      };
    });
    setReturnItems(items);
  };

  const handlePosAddReturnItem = (pendingItem) => {
    const existingIdx = returnItems.findIndex(item => item.issue_transaction_id === pendingItem.issue_transaction_id);
    if (existingIdx > -1) {
      const updated = [...returnItems];
      const currentQty = parseFloat(updated[existingIdx].returnQty) || 0;
      const pendingQty = parseFloat(pendingItem.pending_qty) || 0;
      if (currentQty >= pendingQty) return;
      const newQty = currentQty + 1;
      const rent = parseFloat(pendingItem.rate) || 0;
      const days = parseFloat(updated[existingIdx].days) || 1;
      updated[existingIdx].returnQty = newQty;
      updated[existingIdx].amount = newQty * rent * days;
      setReturnItems(updated);
    } else {
      const rent = parseFloat(pendingItem.rate) || 0;
      const days = calculateDays(pendingItem.issue_date, transactionDate);
      const newQty = 1;
      const amount = newQty * rent * days;
      const newReturnItem = {
        issue_transaction_id: pendingItem.issue_transaction_id,
        issueDate: pendingItem.issue_date,
        material_id: pendingItem.material_id,
        material_name: pendingItem.material_name,
        pending: pendingItem.pending_qty,
        returnQty: newQty,
        days: days,
        rent: rent,
        amount: amount
      };
      setReturnItems([...returnItems, newReturnItem]);
    }
  };

  const handlePosUpdateReturnQty = (idx, change) => {
    const updated = [...returnItems];
    const currentQty = parseFloat(updated[idx].returnQty) || 0;
    const newQty = currentQty + change;
    if (newQty <= 0) {
      setReturnItems(returnItems.filter((_, i) => i !== idx));
    } else {
      const pending = parseFloat(updated[idx].pending) || 0;
      if (newQty > pending) return;
      const rent = parseFloat(updated[idx].rent) || 0;
      const days = parseFloat(updated[idx].days) || 1;
      updated[idx].returnQty = newQty;
      updated[idx].amount = newQty * rent * days;
      setReturnItems(updated);
    }
  };

  const handlePosChangeReturnQtyDirect = (idx, val) => {
    const updated = [...returnItems];
    const pending = parseFloat(updated[idx].pending) || 0;
    let numVal = parseFloat(val) || 0;
    if (numVal > pending) {
      numVal = pending;
      val = pending;
    }
    const rent = parseFloat(updated[idx].rent) || 0;
    const days = parseFloat(updated[idx].days) || 1;
    updated[idx].returnQty = val;
    updated[idx].amount = numVal * rent * days;
    setReturnItems(updated);
  };

  const handlePosUpdateReturnDays = (idx, newDays) => {
    const updated = [...returnItems];
    const d = parseFloat(newDays) || 1;
    const q = parseFloat(updated[idx].returnQty) || 0;
    const r = parseFloat(updated[idx].rent) || 0;
    updated[idx].days = d;
    updated[idx].amount = q * r * d;
    setReturnItems(updated);
  };

  const handlePosUpdateReturnRent = (idx, newRent) => {
    const updated = [...returnItems];
    const r = parseFloat(newRent) || 0;
    const q = parseFloat(updated[idx].returnQty) || 0;
    const d = parseFloat(updated[idx].days) || 0;
    updated[idx].rent = newRent;
    updated[idx].amount = q * r * d;
    setReturnItems(updated);
  };

  // Initial Data Load
  useEffect(() => {
    const fetchBaseData = async () => {
      try {
        const [custRes, matRes, bankRes] = await Promise.all([
          apiFetch('/api/customers'),
          apiFetch('/api/materials'),
          apiFetch('/api/accounts')
        ]);
        if (custRes.ok) {
          const custData = await custRes.ok ? await custRes.json() : [];
          if (Array.isArray(custData)) {
            custData.sort((a, b) => a.name.localeCompare(b.name, 'mr'));
            setCustomers(custData);
          }
        }
        if (matRes.ok) {
          const allMats = await matRes.json();
          // ONLY load Rental Materials for Customer Transaction
          const rentalMats = allMats.filter(m => (m.category || 'Rental Material') === 'Rental Material');
          setMaterials(rentalMats);
        }
        if (bankRes.ok) {
          const allAccs = await bankRes.json();
          const banks = allAccs.filter(acc => acc.group?.name === 'बँक खाते');
          setBankAccounts(banks);
          if (banks.length === 1) {
            setPaymentData(prev => ({ ...prev, bank_account_id: banks[0].id }));
          }
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchBaseData();
  }, []);

  // Fetch Ledger when customer or dates change
  useEffect(() => {
    if (customer) {
      fetchLedger(customer.id);
    } else {
      setLedger([]);
    }
  }, [customer, startDate, endDate]);

  // Local state for robust balances
  const [periodOpeningBalance, setPeriodOpeningBalance] = useState(0);
  const [totalOutstanding, setTotalOutstanding] = useState(0);

  const fetchLedger = async (customerId) => {
    setLoading(true);
    try {
      const challanRes = await apiFetch(`/api/challans?customer_id=${customerId}&start_date=${startDate}&end_date=${endDate}`);

      if (challanRes.ok) {
        const data = await challanRes.json();
        // Check if new robust API format or old array format
        if (data.challans !== undefined) {
          // Sort challans by date ascending, and then by id ascending
          const sortedChallans = [...data.challans].sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            if (dateA - dateB !== 0) return dateA - dateB;
            return a.id - b.id;
          });
          setLedger(sortedChallans);
          setPeriodOpeningBalance(data.period_opening_balance);
          setTotalOutstanding(data.total_outstanding);
        } else {
          const sortedData = [...data].sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            if (dateA - dateB !== 0) return dateA - dateB;
            return a.id - b.id;
          });
          setLedger(sortedData);
          setPeriodOpeningBalance(customer ? customer.opening_balance : 0);
          setTotalOutstanding(customer ? customer.current_balance : 0);
        }
      }
    } catch (err) {
      setError('हिशोब लोड करताना त्रुटी आली.');
    } finally {
      setLoading(false);
    }
  };

  // Open Drawer Setup
  const openDrawer = (section) => {
    if (!customer) {
      alert('कृपया आधी ग्राहक निवडा!');
      return;
    }
    setIsEditMode(false);
    setEditChallanId(null);
    setActiveSection(section);
    setTransactionDate(getTodayDate());
    setError('');
    setOtherCharges([]);

    if (section === 'issue') {
      setCartItems([{ material: null, qty: '' }]);
      setSiteLocation('');
      setReceiptNo('');
      setVehicleNo('');
      setReference('');
    } else if (section === 'payment') {
      setPaymentData({
        cash: '',
        online: '',
        remark: '',
        bank_account_id: bankAccounts.length > 0 ? bankAccounts[0].id : ''
      });
      setPaymentType('credit');
      setPaymentOtherCharges([{ name: '', amount: '' }]);
    }

    setDrawerOpen(true);
  };

  // Open Smart Drawer in Edit Mode for any Challan group
  const openSmartDrawerForEdit = (group) => {
    setIsEditMode(true);
    setEditChallanId(group.id);
    setTransactionDate(group.date || getTodayDate());
    setSiteLocation(group.site_location || '');
    setReceiptNo(group.receipt_no || '');
    setVehicleNo(group.vehicle_no || '');
    setReference(group.reference || '');
    setError('');

    if (group.type === 'Issue') {
      setActiveSection('issue');
      // Populate cartItems
      const items = group.transactions.map(t => ({
        material: materials.find(m => m.id === t.material_id) || t.material || null,
        qty: t.qty_out || ''
      }));
      setCartItems(items.length > 0 ? items : [{ material: null, qty: '' }]);
    }
    else if (group.type === 'Return') {
      setActiveSection('return');
      setOtherCharges(group.other_charges || []);
      // Populate returnItems
      const items = group.transactions.map(t => {
        // Find corresponding issue date if any, or default to group date
        const issueDate = t.issue_transaction?.date || group.date;
        const days = t.days || calculateDays(issueDate, group.date);
        return {
          issue_transaction_id: t.issue_transaction_id,
          issueDate: issueDate,
          material_id: t.material_id,
          material_name: t.material ? t.material.name : 'तपशील नाही',
          pending: t.qty_in, // allow reducing return quantity or keeping it same
          returnQty: t.qty_in,
          days: days,
          rent: t.rate,
          amount: t.debit
        };
      });
      setReturnItems(items);
    }
    else if (group.type === 'Payment') {
      setActiveSection('payment');
      setPaymentType('credit');
      const firstTxn = (group.transactions && group.transactions[0]) || {};
      setPaymentData({
        cash: firstTxn.cash_pay || group.cash_pay || '',
        online: firstTxn.online_pay || group.online_pay || '',
        remark: group.notes || '',
        bank_account_id: group.bank_account_id || ''
      });
    }

    setDrawerOpen(true);
  };

  // Specific Return Setup
  const openSpecificReturn = (txn) => {
    setIsEditMode(false);
    setEditChallanId(null);
    setActiveSection('return');
    const today = getTodayDate();
    setTransactionDate(today);
    setError('');
    setOtherCharges([]);

    const calcDays = calculateDays(txn.date, today);

    setReturnItems([{
      issue_transaction_id: txn.id,
      issueDate: txn.date,
      material_id: txn.material_id,
      material_name: txn.material ? txn.material.name : 'तपशील नाही',
      pending: txn.pending_qty,
      returnQty: '',
      days: calcDays,
      rent: txn.rate,
      amount: 0
    }]);

    setDrawerOpen(true);
  };

  // Delete Challan
  const deleteChallan = async (challanId) => {
    if (!window.confirm('तुम्हाली हे चालान नक्की डिलीट करायचे आहे का? (सर्व साहित्य/पेमेंट रिवर्स होतील)')) return;
    setLoading(true);
    try {
      const res = await apiFetch(`/api/challans/${challanId}`, { method: 'DELETE' });
      if (res.ok) {
        await fetchLedger(customer.id);
        setCartItems([{ material: null, qty: '' }]);
        setSiteLocation(''); // Reset site location
        // Also refresh customer to get latest balance
        const custRes = await apiFetch('/api/customers');
        const custData = await custRes.json();
        if (Array.isArray(custData)) {
          custData.sort((a, b) => a.name.localeCompare(b.name, 'mr'));
          setCustomers(custData);
          setCustomer(custData.find(c => c.id === customer.id));
        }
      } else {
        alert('डिलीट करताना अडचण आली.');
      }
    } catch (err) {
      alert('सर्व्हर त्रुटी.');
    } finally {
      setLoading(false);
    }
  };

  const handleReturnFieldChange = (idx, field, val) => {
    const newItems = [...returnItems];
    newItems[idx][field] = val;

    if (field === 'returnQty' || field === 'rent' || field === 'days') {
      const q = parseFloat(newItems[idx].returnQty) || 0;
      const r = parseFloat(newItems[idx].rent) || 0;
      const d = parseFloat(newItems[idx].days) || 0;
      // Prevent returning more than pending
      if (field === 'returnQty' && q > newItems[idx].pending) {
        newItems[idx].returnQty = newItems[idx].pending;
        newItems[idx].amount = newItems[idx].pending * r * d;
      } else {
        newItems[idx].amount = q * r * d;
      }
    }
    setReturnItems(newItems);
  };

  const totalReturnAmount = returnItems.reduce((sum, item) => sum + (item.amount || 0), 0);

  const handleAddOtherCharge = () => {
    setOtherCharges([...otherCharges, { name: '', amount: '' }]);
  };

  const handleUpdateOtherCharge = (idx, field, val) => {
    const updated = [...otherCharges];
    updated[idx][field] = val;
    setOtherCharges(updated);
  };

  const handleRemoveOtherCharge = (idx) => {
    setOtherCharges(otherCharges.filter((_, i) => i !== idx));
  };

  const totalOtherChargesAmount = otherCharges.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);

  // SAVE TRANSACTION
  const handleSave = async (overrideSection = null) => {
    setLoading(true);
    setError('');

    let payload = {
      customer_id: customer.id,
      date: transactionDate,
      notes: ''
    };

    const section = (overrideSection && typeof overrideSection === 'string') ? overrideSection : activeSection;

    if (section === 'issue') {
      const validItems = cartItems.filter(c => c.material && parseFloat(c.qty) > 0);
      if (validItems.length === 0) {
        setError('कृपया साहित्याची आणि नगांची योग्य माहिती भरा.');
        setLoading(false); return;
      }
      payload.type = 'Issue';
      payload.transactions = validItems.map(c => ({
        material_id: c.material.id,
        qty: parseFloat(c.qty)
      }));
      payload.site_location = siteLocation;
      payload.receipt_no = receiptNo;
      payload.vehicle_no = vehicleNo;
      payload.reference = reference;

    } else if (section === 'return') {
      const validItems = returnItems.filter(r => parseFloat(r.returnQty) > 0);
      if (validItems.length === 0) {
        setError('कृपया जमा करण्यासाठी योग्य नग नोंदवा.');
        setLoading(false); return;
      }
      payload.type = 'Return';
      payload.transactions = validItems.map(r => ({
        issue_transaction_id: r.issue_transaction_id,
        material_id: r.material_id,
        qty: parseFloat(r.returnQty),
        days: r.days,
        rate: parseFloat(r.rent) || 0
      }));
      payload.other_charges = otherCharges.filter(c => c.name && parseFloat(c.amount) > 0).map(c => ({
        name: c.name,
        amount: parseFloat(c.amount)
      }));
    } else if (section === 'payment') {
      if (paymentType === 'debit') {
        const validCharges = paymentOtherCharges.filter(c => c.name && parseFloat(c.amount) > 0);
        if (validCharges.length === 0) {
          setError('कृपया आकार तपशील आणि रक्कम भरा.');
          setLoading(false); return;
        }
        payload.type = 'Return';
        payload.transactions = [];
        payload.other_charges = validCharges.map(c => ({
          name: c.name,
          amount: parseFloat(c.amount)
        }));
      } else {
        if (!paymentData.cash && !paymentData.online) {
          setError('कृपया रक्कम भरा.');
          setLoading(false); return;
        }
        payload.type = 'Payment';
        payload.cash_amount = parseFloat(paymentData.cash || 0);
        payload.online_amount = parseFloat(paymentData.online || 0);
        payload.bank_account_id = paymentData.bank_account_id;
        payload.notes = paymentData.remark;
      }
    }

    try {
      const res = await apiFetch(isEditMode ? `/api/challans/${editChallanId}` : '/api/challans', {
        method: isEditMode ? 'PUT' : 'POST',
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        const savedChallan = await res.json();
        setDrawerOpen(false);
        setPosOpen(false);
        // Refresh customer object to get updated balance
        const custRes = await apiFetch('/api/customers');
        const custData = await custRes.json();
        let updatedBalance = 0;
        if (Array.isArray(custData)) {
          custData.sort((a, b) => a.name.localeCompare(b.name, 'mr'));
          setCustomers(custData);
          const found = custData.find(c => c.id === customer.id);
          setCustomer(found);
          updatedBalance = found ? found.current_balance : 0;
        }
        fetchLedger(customer.id);

        // ── WhatsApp Notification Logic ─────────────────────────────────
        let messageOpened = false;
        if (sendWhatsApp && customer.mobile && savedChallan && savedChallan.id) {
          const pdfUrl = `${window.location.origin}/api/challans/${savedChallan.id}/pdf`;
          let message = '';

          if (section === 'issue') {
            const itemsText = cartItems.filter(c => c.material && c.qty > 0).map(c => `• ${c.material.name}: ${c.qty} नग`).join('\n');
            message = `नमस्ते ${customer.name},\nआपण आज खालील साहित्य नेले आहे:\n${itemsText}\nएकूण येणे: ₹${updatedBalance}\n\nआपले अधिकृत पावती/चलन पाहण्यासाठी खालील लिंकवर क्लिक करा:\n${pdfUrl}`;
          } else if (section === 'return') {
            const itemsText = returnItems.filter(r => r.returnQty > 0).map(r => `• ${r.material_name}: ${r.returnQty} नग`).join('\n');
            let chargesText = '';
            if (totalOtherChargesAmount > 0) {
              chargesText = '\nइतर आकार:\n' + otherCharges.filter(c => c.name && parseFloat(c.amount) > 0).map(c => `• ${c.name}: ₹${c.amount}`).join('\n');
            }
            message = `नमस्ते ${customer.name},\nआपण आज खालील साहित्य जमा केले:\n${itemsText}\nभाडे: ₹${totalReturnAmount.toFixed(0)}${chargesText}\nएकूण येणे: ₹${updatedBalance}\n\nआपली अधिकृत पावती पाहण्यासाठी खालील लिंकवर क्लिक करा:\n${pdfUrl}`;
          } else if (section === 'payment') {
            if (paymentType === 'debit') {
              const totalCharges = paymentOtherCharges.reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0);
              const chargesText = paymentOtherCharges.filter(c => c.name && parseFloat(c.amount) > 0).map(c => `• ${c.name}: ₹${c.amount}`).join('\n');
              message = `नमस्ते ${customer.name},\nआपल्या खात्यावर खालील आकार नोंदवले आहेत:\n${chargesText}\nएकूण नावे: ₹${totalCharges}\nएकूण येणे: ₹${updatedBalance}\n\nआपली अधिकृत पावती पाहण्यासाठी खालील लिंकवर क्लिक करा:\n${pdfUrl}`;
            } else {
              const totalP = (parseFloat(paymentData.cash) || 0) + (parseFloat(paymentData.online) || 0);
              message = `नमस्ते ${customer.name},\nआपले ₹${totalP} यशस्वीरित्या जमा झाले आहेत.\nएकूण येणे: ₹${updatedBalance}\n\nआपली पेमेंट पावती पाहण्यासाठी खालील लिंकवर क्लिक करा:\n${pdfUrl}`;
            }
          }

          if (message) {
            const encodedMsg = encodeURIComponent(message);
            const waUrl = `https://wa.me/91${customer.mobile}?text=${encodedMsg}`;
            setWhatsappShareData({
              url: waUrl,
              customerName: customer.name,
              challanId: savedChallan.id,
              type: section === 'issue' ? 'चलन (Issue Challan)' : section === 'return' ? 'जमा पावती (Return Slip)' : (paymentType === 'debit' ? 'नावे नोंद (Debit Note)' : 'पेमेंट पावती (Payment Receipt)')
            });
            messageOpened = true;
          }
        }

        if (!messageOpened) {
          alert('व्यवहार यशस्वीरित्या जतन केला!');
        }
      } else {
        const err = await res.json();
        setError(err.error || 'व्यवहार जतन करताना त्रुटी आली.');
      }
    } catch (err) {
      setError('सर्व्हरशी संपर्क होऊ शकला नाही.');
    } finally {
      setLoading(false);
    }
  };

  // Open Edit Drawer (Issue type only)
  const openEditDrawer = (group) => {
    setEditChallan(group);
    setEditForm({
      date: group.date || '',
      site_location: group.site_location || '',
      receipt_no: group.receipt_no || '',
      vehicle_no: group.vehicle_no || '',
      reference: group.reference || '',
      notes: group.notes || '',
    });
    setEditError('');
    setEditDrawerOpen(true);
  };

  // Save Issue Challan Edit
  const handleUpdate = async () => {
    setEditLoading(true);
    setEditError('');
    try {
      const res = await apiFetch(`/api/challans/${editChallan.id}`, {
        method: 'PUT',
        body: JSON.stringify(editForm)
      });
      if (res.ok) {
        setEditDrawerOpen(false);
        fetchLedger(customer.id);
      } else {
        const err = await res.json();
        setEditError(err.error || 'Update करताना त्रुटी आली.');
      }
    } catch (err) {
      setEditError('सर्व्हरशी संपर्क होऊ शकला नाही.');
    } finally {
      setEditLoading(false);
    }
  };

  // Open कच्चे बिल Dialog
  const openBillDialog = () => {
    if (!customer) { alert('कृपया आधी ग्राहक निवडा!'); return; }
    const today = getTodayDate();
    setBillAsOfDate(today);
    setBillItems([]);
    setBillTotal(0);
    setBillCustomerBalance(0);
    setBillGrandTotal(0);
    setBillDialogOpen(true);
    setBillLoading(true);
    apiFetch(`/api/challans/pending-bill?customer_id=${customer.id}&as_of_date=${today}`)
      .then(r => r.json())
      .then(data => {
        setBillItems(data.items || []);
        setBillTotal(data.total_estimate || 0);
        setBillCustomerBalance(data.customer_balance || 0);
        setBillGrandTotal(data.grand_total || 0);
      })
      .catch(console.error)
      .finally(() => setBillLoading(false));
  };

  const fetchPendingBill = () => {
    setBillLoading(true);
    apiFetch(`/api/challans/pending-bill?customer_id=${customer.id}&as_of_date=${billAsOfDate}`)
      .then(r => r.json())
      .then(data => {
        setBillItems(data.items || []);
        setBillTotal(data.total_estimate || 0);
        setBillCustomerBalance(data.customer_balance || 0);
        setBillGrandTotal(data.grand_total || 0);
      })
      .catch(console.error)
      .finally(() => setBillLoading(false));
  };

  // WhatsApp – कच्चे बिल
  const sendBillWhatsApp = () => {
    if (!customer?.mobile) return;
    const lines = billItems.map(i =>
      `• ${i.material_name}: ${i.pending_qty} नग × ₹${i.rate} × ${i.days} दिवस = ₹${i.estimate}`
    ).join('\n');
    const hasPendingItems = billItems.length > 0;
    const hasBalance = billCustomerBalance > 0;
    let msg = `नमस्ते ${customer.name},\n\n${formatDate(billAsOfDate)} पर्यंतचे कच्चे बिल:\n`;
    if (hasPendingItems) {
      msg += `\n📦 अजून साहित्य आपल्याकडे आहे (अंदाजे भाडे):\n${lines}\nसाहित्य भाडे एकूण: ₹${billTotal}`;
    }
    if (hasBalance) {
      msg += `\n\n💰 इतर थकित शिल्लक (आधीचे देणे): ₹${billCustomerBalance}`;
    }
    msg += `\n\n✨ एकूण कच्चे बिल: ₹${billGrandTotal}\n\n(हे कच्चे बिल आहे, अंतिम नाही)`;
    window.open(`https://wa.me/91${customer.mobile}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  // Open कच्चे बिल २ Dialog
  const openBill2Dialog = () => {
    if (!customer) { alert('कृपया आधी ग्राहक निवडा!'); return; }
    const today = getTodayDate();
    setBillAsOfDate(today);
    setBillItems([]);
    setBillTotal(0);
    setBillCustomerBalance(0);
    setBillGrandTotal(0);
    setBill2DialogOpen(true);
    setBillLoading(true);
    apiFetch(`/api/challans/pending-bill?customer_id=${customer.id}&as_of_date=${today}`)
      .then(r => r.json())
      .then(data => {
        setBillItems(data.items || []);
        setBillTotal(data.total_estimate || 0);
        setBillCustomerBalance(data.customer_balance || 0);
        setBillGrandTotal(data.grand_total || 0);
      })
      .catch(console.error)
      .finally(() => setBillLoading(false));
  };

  // WhatsApp – कच्चे बिल २ (Combined with PDF and Statement PDF links)
  const sendBill2WhatsApp = () => {
    if (!customer?.mobile) return;
    const lines = billItems.map(i =>
      `• ${i.material_name}: ${i.pending_qty} नग × ₹${i.rate} × ${i.days} दिवस = ₹${i.estimate}`
    ).join('\n');
    const hasPendingItems = billItems.length > 0;
    const hasBalance = billCustomerBalance > 0;
    let msg = `नमस्ते ${customer.name},\n\n${formatDate(billAsOfDate)} पर्यंतचे कच्चे बिल २:\n`;
    if (hasPendingItems) {
      msg += `\n📦 अजून साहित्य आपल्याकडे आहे (अंदाजे भाडे):\n${lines}\nसाहित्य भाडे एकूण: ₹${billTotal}`;
    }
    if (hasBalance) {
      msg += `\n\n💰 इतर थकित शिल्लक (आधीचे देणे): ₹${billCustomerBalance}`;
    }
    msg += `\n\n✨ एकूण कच्चे बिल: ₹${billGrandTotal}\n\n(हे कच्चे बिल आहे, अंतिम नाही)`;
    
    const branchId = activeBranch?.id;
    const branchFilter = branchId ? `&branch_id=${branchId}` : '';
    const pendingBillPdfUrl = `${window.location.origin}/api/challans/pending-bill/pdf?customer_id=${customer.id}&as_of_date=${billAsOfDate}${branchFilter}`;
    const ledgerPdfUrl = `${window.location.origin}/api/customers/${customer.id}/statement-pdf?start_date=2024-04-01&end_date=${billAsOfDate}${branchFilter}`;
    
    msg += `\n\n📄 कच्चे बिल PDF डाउनलोड करा:\n${pendingBillPdfUrl}`;
    msg += `\n\n📄 सविस्तर खाते उतारा (Ledger) PDF:\n${ledgerPdfUrl}`;

    window.open(`https://wa.me/91${customer.mobile}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f4f6f8' }} className="ledger-container">
      <PrintHeader tenant={tenant} branch={activeBranch} customer={customer} startDate={formatDate(startDate)} endDate={formatDate(endDate)} />
      <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid var(--primary-green)' }} className="no-print">
        <Typography variant="h5" sx={{ color: '#1B5E20', fontWeight: 'bold' }}>ग्राहक व्यवहार (Ledger)</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<LocalShippingIcon />}
            onClick={() => {
              setPosActiveTab('issue');
              setPosOpen(true);
              if (customer) {
                fetchPosPendingItems(customer.id);
              }
            }}
            sx={{ backgroundColor: '#E65100', fontWeight: 'bold' }}
          >
            साहित्य दिले/घेतले
          </Button>
          <Button variant="contained" size="small" startIcon={<LocalShippingIcon />} onClick={() => openDrawer('issue')} sx={{ display: 'none', backgroundColor: '#2E7D32', fontWeight: 'bold' }}>
            साहित्य दिले (+)
          </Button>
          <Button variant="contained" size="small" startIcon={<PaymentIcon />} onClick={() => openDrawer('payment')} sx={{ backgroundColor: '#1565C0', fontWeight: 'bold' }}>
            जमा/नावे नोंद
          </Button>
          <Button variant="contained" size="small" onClick={openBillDialog}
            sx={{ display: 'none', backgroundColor: '#6A1B9A', fontWeight: 'bold' }}>
            📋 कच्चे बिल
          </Button>
          <Button variant="contained" size="small" onClick={openBill2Dialog}
            sx={{ backgroundColor: '#8E24AA', fontWeight: 'bold' }}>
            📋 कच्चे बिल २
          </Button>
          <Button
            variant="outlined" size="small" startIcon={<PictureAsPdfIcon />}
            onClick={() => window.print()}
            sx={{ borderColor: '#1B5E20', color: '#1B5E20', fontWeight: 'bold' }}
          >
            खाते उतारा (Print)
          </Button>
        </Box>
      </Box>

      {/* SEARCH BAR */}
      <Paper sx={{ m: 2, p: 2, borderRadius: 2 }} className="no-print">
        <Grid container spacing={2} alignItems="center">

          {/* Row 1: Search Customer (Left, wide) */}
          <Grid item xs={12} md={8}>
            <Autocomplete
              fullWidth
              options={customers}
              getOptionLabel={(option) => `${option.name} - ${option.mobile || 'No Mobile'}`}
              value={customer}
              onChange={(e, val) => setCustomer(val)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="ग्राहकाचे नाव शोधा..."
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '1.25rem',
                      fontWeight: 'bold',
                      color: '#1B5E20'
                    }
                  }}
                />
              )}
            />
          </Grid>

          {/* Row 1 Right: Selected Customer Info */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {customer && (
                <Box
                  component="img"
                  src={customer.photo_path ? `${IMAGE_BASE_URL}/${customer.photo_path}` : 'https://via.placeholder.com/60'}
                  onError={(e) => { e.target.src = 'https://via.placeholder.com/60'; }}
                  sx={{ width: 45, height: 45, borderRadius: '50%', border: '2px solid #1B5E20', objectFit: 'cover', flexShrink: 0 }}
                />
              )}
              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>निवडलेला ग्राहक:</Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1B5E20', lineHeight: 1.2 }}>
                  {customer?.name || '---'}
                </Typography>
                {customer?.mobile && (
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.2 }}>
                    {customer.mobile}
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>
          {/* Row 2: Divider */}
          <Grid item xs={12}>
            <Divider sx={{ my: 0.5 }} />
          </Grid>

          {/* Row 3: Details & Filters Below */}
          {/* सध्याची शिल्लक */}
          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>सध्याची शिल्लक (Current Balance):</Typography>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: totalOutstanding > 0 ? '#D32F2F' : '#1B5E20' }}>
              ₹ {totalOutstanding}
            </Typography>
          </Grid>

          {/* Start Date */}
          <Grid item xs={6} md={3}>
            <TextField fullWidth label="येथून (Start Date)" type="date" size="small"
              value={startDate} onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }} />
          </Grid>

          {/* End Date */}
          <Grid item xs={6} md={3}>
            <TextField fullWidth label="येथपर्यंत (End Date)" type="date" size="small"
              value={endDate} onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }} />
          </Grid>

        </Grid>
      </Paper>

      {/* LEDGER TABLE */}
      <Box sx={{ p: 2 }}>
        <Paper elevation={0} sx={{ border: '1px solid #ddd', borderRadius: 2, overflow: 'hidden' }}>
          <div className="professional-table-wrapper">
            <table className="professional-table">
              <thead>
                <tr>
                  <th>चलान / पावती नं.</th>
                  <th>दिनांक</th>
                  <th style={{ width: '20%' }}>तपशील / साहित्य</th>
                  <th>दिलेले नग</th>
                  <th>पेंडिंग नग</th>
                  <th>जमा नग</th>
                  <th>दर (₹)</th>
                  <th>दिवस</th>
                  <th>रिमार्क / साइट</th>

                  <th>नावे रक्कम</th>
                  <th>रोख/ऑनलाइन जमा</th>
                  <th>सद्य शिल्लक</th>
                  <th>अॅक्शन</th>
                </tr>
              </thead>
              <tbody>
                {loading && ledger.length === 0 && (
                  <tr><td colSpan={13} align="center" style={{ padding: 20 }}><CircularProgress /></td></tr>
                )}
                {!loading && ledger.length === 0 && (
                  <tr><td colSpan={13} align="center" style={{ padding: 20 }}>कोणताही हिशोब उपलब्ध नाही.</td></tr>
                )}

                {/* Explicit Opening Balance Row */}
                {!loading && customer && (
                  <tr style={{ backgroundColor: '#e8f5e9' }}>
                    <td colSpan={11} align="right" style={{ fontWeight: 'bold' }}>आरंभीची शिल्लक (Opening Balance):</td>
                    <td align="right" style={{ fontWeight: 'bold', color: '#1B5E20' }}>{periodOpeningBalance}</td>
                    <td></td>
                  </tr>
                )}

                {/* Build Rows from Nested Data with Dynamic Running Balance */}
                {(() => {
                  let runningBalance = parseFloat(periodOpeningBalance || 0);

                  return ledger.map((group) => {
                    // Determine display type
                    let typeColor = '#2E7D32';
                    let typeLabel = 'साहित्य दिले';
                    if (group.type === 'Return') { typeColor = '#E65100'; typeLabel = 'साहित्य जमा'; }
                    if (group.type === 'Payment') { typeColor = '#1565C0'; typeLabel = 'जमा रक्कम'; }

                    return group.transactions.map((txn, iIdx) => (
                      <tr key={txn.id}>
                        {iIdx === 0 && (
                          <td rowSpan={group.transactions.length} align="center" style={{ fontWeight: 'bold', backgroundColor: '#f9f9f9', borderRight: '1px solid #ddd' }}>
                            #{group.id}
                            {group.receipt_no && <Typography variant="caption" sx={{ display: 'block', color: '#555', fontSize: '0.65rem' }}>पावती: {group.receipt_no}</Typography>}
                          </td>
                        )}
                        {iIdx === 0 && (
                          <td rowSpan={group.transactions.length} align="center" style={{ backgroundColor: '#f9f9f9', borderRight: '1px solid #ddd' }}>
                            {formatDate(group.date)}
                          </td>
                        )}
                        <td>
                          {iIdx === 0 && <Typography variant="caption" sx={{ fontWeight: 'bold', color: typeColor, display: 'block' }}>{typeLabel}</Typography>}
                          <Typography variant="body2">{txn.material ? txn.material.name : (txn.notes || group.notes || '---')}</Typography>
                        </td>
                        <td align="center">{txn.qty_out > 0 ? txn.qty_out : '-'}</td>
                        <td align="center">
                          {txn.type === 'Issue' && txn.pending_qty > 0 && (
                            <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>{txn.pending_qty}</span>
                          )}
                          {txn.type === 'Issue' && txn.pending_qty === 0 && (
                            <span style={{ color: 'green' }}>०</span>
                          )}
                          {txn.type !== 'Issue' && '-'}
                        </td>
                        <td align="center">{txn.qty_in > 0 ? txn.qty_in : '-'}</td>
                        <td align="center">{txn.rate > 0 ? txn.rate : '-'}</td>
                        <td align="center">{txn.days > 0 ? txn.days : '-'}</td>
                        <td style={{ fontSize: '0.75rem', color: '#666' }}>
                          {group.site_location && <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold', color: '#1B5E20' }}>📍 {group.site_location}</Typography>}
                          {group.vehicle_no && <Typography variant="caption" sx={{ display: 'block', color: '#555' }}>🚚 {group.vehicle_no}</Typography>}
                          {group.reference && <Typography variant="caption" sx={{ display: 'block', color: '#777' }}>👤 {group.reference}</Typography>}
                          {txn.notes || group.notes || ''}
                        </td>

                        <td align="right" style={{ color: '#d32f2f' }}>{txn.debit > 0 ? txn.debit : '-'}</td>
                        <td align="right" style={{ color: '#2e7d32' }}>
                          {txn.credit > 0 ? (
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>₹ {txn.credit}</Typography>
                              {(txn.cash_pay > 0 || txn.online_pay > 0) && (
                                <Typography variant="caption" sx={{ color: '#666', display: 'block', fontSize: '0.65rem' }}>
                                  {txn.cash_pay > 0 && `रोख: ₹${txn.cash_pay}`}
                                  {txn.cash_pay > 0 && txn.online_pay > 0 && ' | '}
                                  {txn.online_pay > 0 && `Online: ₹${txn.online_pay}`}
                                </Typography>
                              )}
                            </Box>
                          ) : '-'}
                        </td>
                        <td align="right" style={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>
                          {(() => {
                            runningBalance = runningBalance + (parseFloat(txn.debit) || 0) - (parseFloat(txn.credit) || 0);
                            return runningBalance;
                          })()}
                        </td>
                        <td align="center">
                          {txn.type === 'Issue' && txn.pending_qty > 0 && (
                            <Button variant="contained" size="small" color="warning"
                              sx={{ fontSize: '0.65rem', py: 0.2 }}
                              startIcon={<KeyboardReturnIcon />}
                              onClick={() => openSpecificReturn(txn)}>
                              जमा पूर्ण करा
                            </Button>
                          )}
                          {iIdx === 0 && (
                            <IconButton size="small" color="primary"
                              onClick={() => openSmartDrawerForEdit(group)}
                              sx={{ ml: 0.5 }} title="व्यवहार Edit करा">
                              <EditIcon fontSize="small" />
                            </IconButton>
                          )}
                          {iIdx === 0 && (
                            <IconButton size="small" color="success"
                              onClick={() => window.open(`${window.location.origin}/api/challans/${group.id}/pdf`, '_blank')}
                              sx={{ ml: 0.5 }} title="पावती / चलन PDF">
                              <PictureAsPdfIcon fontSize="small" />
                            </IconButton>
                          )}
                          {iIdx === 0 && (
                            <IconButton size="small" color="error"
                              onClick={() => deleteChallan(group.id)}
                              sx={{ ml: 0.5 }} title="चलन डिलीट करा">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </td>
                      </tr>
                    ));
                  });
                })()}
              </tbody>
            </table>
          </div>
        </Paper>
      </Box>

      {/* SMART DRAWER */}
      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)} PaperProps={{ sx: { width: { xs: '100%', sm: 450 }, p: 0 } }}>
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: activeSection === 'issue' ? '#2E7D32' : activeSection === 'return' ? '#E65100' : '#1565C0', color: '#fff' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {isEditMode ? 'व्यवहार संपादित करा (Edit)' : (activeSection === 'issue' ? 'नवीन व्यवहार - साहित्य दिले' : activeSection === 'return' ? 'व्यवहार - साहित्य जमा' : 'जमा / नावे नोंद')}
            </Typography>
            <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
          </Box>

          <Box sx={{ p: 2, flexGrow: 1, overflowY: 'auto' }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <TextField
              fullWidth label="दिनांक" type="date" size="small" sx={{ mb: 3 }}
              value={transactionDate}
              onChange={(e) => {
                const newDate = e.target.value;
                setTransactionDate(newDate);
                // Dynamically update days if we are returning material
                if (activeSection === 'return') {
                  const updatedItems = returnItems.map(item => {
                    if (item.issueDate) {
                      const newDays = calculateDays(item.issueDate, newDate);
                      const amount = (parseFloat(item.returnQty) || 0) * (parseFloat(item.rent) || 0) * newDays;
                      return { ...item, days: newDays, amount: amount };
                    }
                    return item;
                  });
                  setReturnItems(updatedItems);
                }
              }}
              InputLabelProps={{ shrink: true }}
            />

            {/* SECTION 1: ISSUE */}
            {activeSection === 'issue' && (
              <Box sx={{ mt: 2 }}>
                <TextField fullWidth label="साइट लोकेशन (Site Location)" variant="outlined"
                  size="small" value={siteLocation} onChange={(e) => setSiteLocation(e.target.value)}
                  sx={{ mb: 2, bgcolor: '#FFF' }} placeholder="उदा. प्लॉट नं २४, उंब्रज"
                />
                <TextField fullWidth label="पावती नं. (Receipt No.)" variant="outlined"
                  size="small" value={receiptNo} onChange={(e) => setReceiptNo(e.target.value)}
                  sx={{ mb: 2, bgcolor: '#FFF' }} placeholder="उदा. ABC-001"
                />
                <TextField fullWidth label="गाडी नं. (Vehicle No.)" variant="outlined"
                  size="small" value={vehicleNo} onChange={(e) => setVehicleNo(e.target.value)}
                  sx={{ mb: 2, bgcolor: '#FFF' }} placeholder="उदा. MH09 AB 1234"
                />
                <TextField fullWidth label="रेफरन्स / ओळख (Reference)" variant="outlined"
                  size="small" value={reference} onChange={(e) => setReference(e.target.value)}
                  sx={{ mb: 2, bgcolor: '#FFF' }} placeholder="उदा. राम पाटील यांच्याकडून"
                />

                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: '#1B5E20' }}>
                  साहित्य निवडा
                </Typography>

                {cartItems.map((item, idx) => (
                  <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <Autocomplete
                      fullWidth size="small" options={materials}
                      getOptionLabel={(opt) => `${opt.name} (उपलब्ध: ${opt.available_stock} | दर: ${opt.rate_per_day})`}
                      value={item.material}
                      onChange={(e, val) => {
                        const newC = [...cartItems];
                        newC[idx].material = val;
                        setCartItems(newC);
                      }}
                      renderInput={(params) => <TextField {...params} placeholder="साहित्य..." />}
                    />
                    <TextField size="small" placeholder="नग" type="number" sx={{ width: 100 }} value={item.qty} onChange={(e) => {
                      const newC = [...cartItems];
                      newC[idx].qty = e.target.value;
                      setCartItems(newC);
                    }} />
                    <IconButton color="error" size="small" onClick={() => setCartItems(cartItems.filter((_, i) => i !== idx))} disabled={cartItems.length === 1}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                <Button startIcon={<AddCircleOutlineIcon />} size="small" onClick={() => setCartItems([...cartItems, { material: null, qty: '' }])}>आणखी जोडा</Button>
              </Box>
            )}

            {/* SECTION 2: RETURN */}
            {activeSection === 'return' && (
              <Box>
                <Box sx={{ border: '1px solid #eee', borderRadius: 1, overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead style={{ backgroundColor: '#f5f5f5' }}>
                      <tr>
                        <th style={{ padding: '6px', textAlign: 'left' }}>साहित्य</th>
                        <th style={{ padding: '6px' }}>पेंडिंग</th>
                        <th style={{ padding: '6px' }}>परत</th>
                        <th style={{ padding: '6px' }}>दिवस</th>
                        <th style={{ padding: '6px', textAlign: 'right' }}>अमाउंट</th>
                      </tr>
                    </thead>
                    <tbody>
                      {returnItems.map((item, idx) => (
                        <tr key={idx} style={{ borderTop: '1px solid #eee' }}>
                          <td style={{ padding: '6px', fontWeight: 'bold' }}>
                            {item.material_name} <br />
                            <span style={{ fontWeight: 'normal', color: '#666', fontSize: '0.7rem', display: 'inline-flex', alignItems: 'center', marginTop: '2px' }}>
                              दर: ₹
                              <input 
                                type="number" 
                                step="any" 
                                style={{ width: '45px', padding: '2px 4px', border: '1px solid #ccc', borderRadius: 3, marginLeft: '4px', fontSize: '0.7rem', fontWeight: 'bold' }} 
                                value={item.rent} 
                                onChange={(e) => handleReturnFieldChange(idx, 'rent', e.target.value)} 
                              />
                            </span>
                          </td>
                          <td align="center" style={{ fontWeight: 'bold', color: '#D32F2F' }}>{item.pending}</td>
                          <td style={{ padding: '4px' }}>
                            <input type="number" min="0" max={item.pending} style={{ width: '50px', padding: '4px', border: '1px solid #ccc', borderRadius: 3 }} value={item.returnQty} onChange={(e) => handleReturnFieldChange(idx, 'returnQty', e.target.value)} />
                          </td>
                          <td align="center">
                            <input type="number" min="1" style={{ width: '40px', padding: '4px', border: '1px solid #ccc', borderRadius: 3 }} value={item.days} onChange={(e) => handleReturnFieldChange(idx, 'days', e.target.value)} />
                          </td>
                          <td align="right" style={{ padding: '6px', fontWeight: 'bold' }}>{item.amount?.toFixed(0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>

                <Box sx={{ mt: 3, borderTop: '1px solid #ddd', pt: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#E65100', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>इतर आकार (Other Charges)</span>
                    <Button size="small" variant="outlined" color="warning" onClick={handleAddOtherCharge} sx={{ py: 0.2, fontSize: '0.75rem' }}>
                      + चार्ज जोडा
                    </Button>
                  </Typography>

                  {otherCharges.map((charge, idx) => (
                    <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                      <TextField
                        size="small"
                        placeholder="उदा. वाहतूक भाडे, तुटफूट"
                        value={charge.name}
                        onChange={(e) => handleUpdateOtherCharge(idx, 'name', e.target.value)}
                        fullWidth
                        sx={{ bgcolor: '#FFF' }}
                      />
                      <TextField
                        size="small"
                        placeholder="रक्कम"
                        type="number"
                        value={charge.amount}
                        onChange={(e) => handleUpdateOtherCharge(idx, 'amount', e.target.value)}
                        sx={{ width: 100, bgcolor: '#FFF' }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                      />
                      <IconButton color="error" size="small" onClick={() => handleRemoveOtherCharge(idx)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>

                <Box sx={{ mt: 3, p: 2, backgroundColor: '#f1f8e9', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" color="textSecondary">एकूण भाडे (Rent):</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>₹ {totalReturnAmount.toFixed(0)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" color="textSecondary">इतर आकार (Charges):</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>₹ {totalOtherChargesAmount.toFixed(0)}</Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1B5E20' }}>एकूण नावे रक्कम:</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1B5E20' }}>
                      ₹ {(totalReturnAmount + totalOtherChargesAmount).toFixed(0)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {/* SECTION 3: PAYMENT */}
            {activeSection === 'payment' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl component="fieldset" sx={{ mb: 1 }}>
                  <RadioGroup
                    row
                    value={paymentType}
                    onChange={(e) => setPaymentType(e.target.value)}
                  >
                    <FormControlLabel value="credit" control={<Radio color="primary" />} label="जमा रक्कम (Payment In)" />
                    <FormControlLabel value="debit" control={<Radio color="error" />} label="नावे नोंद / इतर आकार (Charges)" />
                  </RadioGroup>
                </FormControl>

                {paymentType === 'credit' ? (
                  <>
                    <TextField fullWidth label="कॅश रक्कम (Cash)" size="small" type="number" value={paymentData.cash} onChange={(e) => setPaymentData({ ...paymentData, cash: e.target.value })} InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
                    <TextField fullWidth label="ऑनलाइन रक्कम (UPI)" size="small" type="number" value={paymentData.online} onChange={(e) => setPaymentData({ ...paymentData, online: e.target.value })} InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />

                    {parseFloat(paymentData.online) > 0 && bankAccounts.length > 1 && (
                      <FormControl fullWidth size="small">
                        <InputLabel>कोणत्या बँकेत जमा झाले?</InputLabel>
                        <Select
                          value={paymentData.bank_account_id}
                          label="कोणत्या बँकेत जमा झाले?"
                          onChange={(e) => setPaymentData({ ...paymentData, bank_account_id: e.target.value })}
                        >
                          {bankAccounts.map(bank => (
                            <MenuItem key={bank.id} value={bank.id}>{bank.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}

                    <TextField fullWidth label="रिमार्क / टीप" size="small" multiline rows={3} placeholder="उदा. कॅश सचिनला दिली..." value={paymentData.remark} onChange={(e) => setPaymentData({ ...paymentData, remark: e.target.value })} />
                  </>
                ) : (
                  <Box sx={{ border: '1px solid #ddd', p: 2, borderRadius: 1, bgcolor: '#fff5f5' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, color: '#D32F2F', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>इतर आकार (Other Charges)</span>
                    </Typography>

                    {paymentOtherCharges.map((charge, idx) => (
                      <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
                        <TextField
                          size="small"
                          placeholder="उदा. वाहतूक भाडे, तुटफूट"
                          value={charge.name}
                          onChange={(e) => {
                            const newC = [...paymentOtherCharges];
                            newC[idx].name = e.target.value;
                            setPaymentOtherCharges(newC);
                          }}
                          fullWidth
                          sx={{ bgcolor: '#FFF' }}
                        />
                        <TextField
                          size="small"
                          placeholder="रक्कम"
                          type="number"
                          value={charge.amount}
                          onChange={(e) => {
                            const newC = [...paymentOtherCharges];
                            newC[idx].amount = e.target.value;
                            setPaymentOtherCharges(newC);
                          }}
                          sx={{ width: 150, bgcolor: '#FFF' }}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            <Box sx={{ mt: 3 }}>
              <FormControlLabel
                control={<Checkbox checked={sendWhatsApp} onChange={(e) => setSendWhatsApp(e.target.checked)} color="success" />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WhatsAppIcon sx={{ color: '#25D366' }} />
                    <Typography variant="body2">WhatsApp मेसेज पाठवा</Typography>
                  </Box>
                }
              />
            </Box>
          </Box>

          <Box sx={{ p: 2, borderTop: '1px solid #eee' }}>
            <Button fullWidth variant="contained" size="large" disabled={loading} sx={{ backgroundColor: activeSection === 'issue' ? '#2E7D32' : activeSection === 'return' ? '#E65100' : '#1565C0', py: 1.5, fontWeight: 'bold' }} onClick={handleSave}>
              {loading ? <CircularProgress size={24} color="inherit" /> : 'सेव्ह करा (SAVE)'}
            </Button>
          </Box>
        </Box>
      </Drawer>



      {/* कच्चे बिल DIALOG */}
      <Dialog open={billDialogOpen} onClose={() => setBillDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#4A148C', color: '#fff', fontWeight: 'bold' }}>
          📋 कच्चे बिल — {customer?.name}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, mt: 1 }}>
            <TextField label="कोणत्या तारखेपर्यंत बघायचे?" type="date" size="small"
              value={billAsOfDate} onChange={(e) => setBillAsOfDate(e.target.value)}
              InputLabelProps={{ shrink: true }} sx={{ width: 220 }} />
            <Button variant="contained" size="small" onClick={fetchPendingBill}
              disabled={billLoading} sx={{ backgroundColor: '#6A1B9A' }}>
              {billLoading ? <CircularProgress size={18} color="inherit" /> : 'हिशोब बघा'}
            </Button>
          </Box>

          {billItems.length === 0 && !billLoading && (
            <Typography color="textSecondary" align="center" sx={{ py: 3 }}>कोणतेही Pending साहित्य नाही.</Typography>
          )}

          {billItems.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead style={{ backgroundColor: '#4A148C', color: '#fff' }}>
                <tr>
                  <th style={{ padding: '8px', textAlign: 'left' }}>साहित्य</th>
                  <th style={{ padding: '8px', textAlign: 'center' }}>दिलेली तारीख</th>
                  <th style={{ padding: '8px', textAlign: 'center' }}>Pending नग</th>
                  <th style={{ padding: '8px', textAlign: 'center' }}>दिवस</th>
                  <th style={{ padding: '8px', textAlign: 'center' }}>दर (₹)</th>
                  <th style={{ padding: '8px', textAlign: 'right' }}>अंदाजे बिल</th>
                </tr>
              </thead>
              <tbody>
                {billItems.map((item, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #eee', backgroundColor: i % 2 === 0 ? '#fff' : '#f9f4ff' }}>
                    <td style={{ padding: '8px', fontWeight: 'bold' }}>{item.material_name}</td>
                    <td style={{ padding: '8px', textAlign: 'center' }}>{formatDate(item.issue_date)}</td>
                    <td style={{ padding: '8px', textAlign: 'center', color: '#d32f2f', fontWeight: 'bold' }}>{item.pending_qty}</td>
                    <td style={{ padding: '8px', textAlign: 'center' }}>{item.days}</td>
                    <td style={{ padding: '8px', textAlign: 'center' }}>{item.rate}</td>
                    <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>₹ {item.estimate}</td>
                  </tr>
                ))}
                {/* Pending Rent Subtotal */}
                <tr style={{ backgroundColor: '#EDE7F6' }}>
                  <td colSpan={5} style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>📦 अजून साहित्य आहे (अंदाजे भाडे):</td>
                  <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold', color: '#4A148C' }}>₹ {billTotal}</td>
                </tr>
                {/* Existing Balance Row */}
                {billCustomerBalance > 0 && (
                  <tr style={{ backgroundColor: '#fff3e0' }}>
                    <td colSpan={5} style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>💰 इतर थकित शिल्लक (जमा साहित्य + आधीचे देणे):</td>
                    <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold', color: '#E65100' }}>₹ {billCustomerBalance}</td>
                  </tr>
                )}
                {/* Grand Total */}
                <tr style={{ backgroundColor: '#4A148C', color: '#fff' }}>
                  <td colSpan={5} style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold', fontSize: '1rem' }}>✨ एकूण कच्चे बिल:</td>
                  <td style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold', fontSize: '1.2rem' }}>₹ {billGrandTotal}</td>
                </tr>
              </tbody>
            </table>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setBillDialogOpen(false)} variant="outlined">बंद करा</Button>
          <Button onClick={sendBillWhatsApp} variant="contained"
            disabled={billItems.length === 0 && billCustomerBalance <= 0}
            startIcon={<WhatsAppIcon />} sx={{ backgroundColor: '#25D366' }}>
            WhatsApp पाठवा (कच्चे बिल)
          </Button>
        </DialogActions>
      </Dialog>

      {/* कच्चे बिल २ DIALOG */}
      <Dialog open={bill2DialogOpen} onClose={() => setBill2DialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#8E24AA', color: '#fff', fontWeight: 'bold' }}>
          📋 कच्चे बिल २ — {customer?.name}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, mt: 1 }}>
            <TextField label="कोणत्या तारखेपर्यंत बघायचे?" type="date" size="small"
              value={billAsOfDate} onChange={(e) => setBillAsOfDate(e.target.value)}
              InputLabelProps={{ shrink: true }} sx={{ width: 220 }} />
            <Button variant="contained" size="small" onClick={fetchPendingBill}
              disabled={billLoading} sx={{ backgroundColor: '#8E24AA' }}>
              {billLoading ? <CircularProgress size={18} color="inherit" /> : 'हिशोब बघा'}
            </Button>
          </Box>

          {billItems.length === 0 && !billLoading && (
            <Typography color="textSecondary" align="center" sx={{ py: 3 }}>कोणतेही Pending साहित्य नाही.</Typography>
          )}

          {billItems.length > 0 && (
            <>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                <thead style={{ backgroundColor: '#8E24AA', color: '#fff' }}>
                  <tr>
                    <th style={{ padding: '8px', textAlign: 'left' }}>साहित्य</th>
                    <th style={{ padding: '8px', textAlign: 'center' }}>दिलेली तारीख</th>
                    <th style={{ padding: '8px', textAlign: 'center' }}>Pending नग</th>
                    <th style={{ padding: '8px', textAlign: 'center' }}>दिवस</th>
                    <th style={{ padding: '8px', textAlign: 'center' }}>दर (₹)</th>
                    <th style={{ padding: '8px', textAlign: 'right' }}>अंदाजे बिल</th>
                  </tr>
                </thead>
                <tbody>
                  {billItems.map((item, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #eee', backgroundColor: i % 2 === 0 ? '#fff' : '#fcf8ff' }}>
                      <td style={{ padding: '8px', fontWeight: 'bold' }}>{item.material_name}</td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>{formatDate(item.issue_date)}</td>
                      <td style={{ padding: '8px', textAlign: 'center', color: '#d32f2f', fontWeight: 'bold' }}>{item.pending_qty}</td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>{item.days}</td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>{item.rate}</td>
                      <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>₹ {item.estimate}</td>
                    </tr>
                  ))}
                  {/* Pending Rent Subtotal */}
                  <tr style={{ backgroundColor: '#F3E5F5' }}>
                    <td colSpan={5} style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>📦 अजून साहित्य आहे (अंदाजे भाडे):</td>
                    <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold', color: '#7B1FA2' }}>₹ {billTotal}</td>
                  </tr>
                  {/* Existing Balance Row */}
                  {billCustomerBalance > 0 && (
                    <tr style={{ backgroundColor: '#fff3e0' }}>
                      <td colSpan={5} style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>💰 इतर थकित शिल्लक (जमा साहित्य + आधीचे देणे):</td>
                      <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold', color: '#E65100' }}>₹ {billCustomerBalance}</td>
                    </tr>
                  )}
                  {/* Grand Total */}
                  <tr style={{ backgroundColor: '#8E24AA', color: '#fff' }}>
                    <td colSpan={5} style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold', fontSize: '1rem' }}>✨ एकूण कच्चे बिल:</td>
                    <td style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold', fontSize: '1.2rem' }}>₹ {billGrandTotal}</td>
                  </tr>
                </tbody>
              </table>

              {/* Composition notice box with statement download button */}
              <Box sx={{ mt: 2, p: 2, bgcolor: '#fffde7', border: '1px solid #ffe082', borderRadius: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.85rem', lineHeight: 1.4 }}>
                  💡 <strong>मागील शिल्लक माहिती:</strong> मागील शिल्लक (₹{billCustomerBalance}) ही पूर्वी जमा केलेल्या साहित्याची आणि केलेल्या पेमेंटची एकत्रित शिल्लक आहे. सविस्तर तपशीलासाठी (कोणते साहित्य, किती दिवस, पेमेंट कधी जमा झाले) खालील बटणावर क्लिक करून खाते उतारा तपासावा.
                </Typography>
                <Box sx={{ textAlign: 'right' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PictureAsPdfIcon />}
                    onClick={() => {
                      const branchId = activeBranch?.id;
                      const branchFilter = branchId ? `&branch_id=${branchId}` : '';
                      const ledgerPdfUrl = `${window.location.origin}/api/customers/${customer.id}/statement-pdf?start_date=2024-04-01&end_date=${billAsOfDate}${branchFilter}`;
                      window.open(ledgerPdfUrl, '_blank');
                    }}
                    sx={{ borderColor: '#E65100', color: '#E65100', '&:hover': { borderColor: '#b53d00', bgcolor: '#fff3e0' }, fontWeight: 'bold' }}
                  >
                    सविस्तर खाते उतारा (Ledger PDF)
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setBill2DialogOpen(false)} variant="outlined">बंद करा</Button>
          <Button
            variant="contained"
            disabled={billItems.length === 0}
            startIcon={<PictureAsPdfIcon />}
            onClick={() => {
              const branchId = activeBranch?.id;
              const branchFilter = branchId ? `&branch_id=${branchId}` : '';
              const pendingBillPdfUrl = `${window.location.origin}/api/challans/pending-bill/pdf?customer_id=${customer.id}&as_of_date=${billAsOfDate}${branchFilter}`;
              window.open(pendingBillPdfUrl, '_blank');
            }}
            sx={{ backgroundColor: '#7B1FA2', color: 'white', '&:hover': { backgroundColor: '#4D1A60' } }}
          >
            कच्चे बिल PDF (Print)
          </Button>
          <Button onClick={sendBill2WhatsApp} variant="contained"
            disabled={billItems.length === 0 && billCustomerBalance <= 0}
            startIcon={<WhatsAppIcon />} sx={{ backgroundColor: '#25D366' }}>
            WhatsApp पाठवा (कच्चे बिल २)
          </Button>
        </DialogActions>
      </Dialog>

      {/* POS FULL SCREEN MODE */}
      <Dialog
        fullScreen
        open={posOpen}
        onClose={() => setPosOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: '#f4f7f6',
            display: 'flex',
            flexDirection: 'column',
            fontFamily: "'Baloo 2', 'Outfit', sans-serif"
          }
        }}
      >
        {/* POS HEADER */}
        <Box sx={{
          background: 'linear-gradient(135deg, #1b5e20, #2e7d32)',
          color: 'white',
          px: 3,
          py: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          zIndex: 10
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {customer && (
              <Box
                component="img"
                src={customer.photo_path ? `${IMAGE_BASE_URL}/${customer.photo_path}` : 'https://via.placeholder.com/60'}
                onError={(e) => { e.target.src = 'https://via.placeholder.com/60'; }}
                sx={{ width: 45, height: 45, borderRadius: '50%', border: '2px solid white', objectFit: 'cover' }}
              />
            )}
            <Box>
              <Autocomplete
                size="small"
                options={customers}
                getOptionLabel={(option) => `${option.name} - ${option.mobile || 'No Mobile'}`}
                value={customer}
                onChange={(e, val) => {
                  setCustomer(val);
                  if (val) {
                    fetchPosPendingItems(val.id);
                  }
                }}
                sx={{
                  width: 320,
                  bgcolor: 'rgba(255,255,255,0.15)',
                  borderRadius: 1,
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    fontWeight: 'bold',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                    '&:hover fieldset': { borderColor: 'white' },
                    '&.Mui-focused fieldset': { borderColor: 'white' }
                  },
                  '& .MuiAutocomplete-clearIndicator': { color: 'white' },
                  '& .MuiAutocomplete-popupIndicator': { color: 'white' }
                }}
                renderInput={(params) => <TextField {...params} placeholder="ग्राहक निवडा..." variant="outlined" />}
              />
            </Box>
            {customer && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  📞 {customer.mobile || 'No Mobile'}
                </Typography>
                <Typography variant="body2" sx={{
                  bgcolor: '#ffebee',
                  color: '#c62828',
                  px: 1,
                  py: 0.2,
                  borderRadius: 1,
                  fontWeight: 'bold',
                  fontSize: '0.85rem'
                }}>
                  येणे: ₹{totalOutstanding}
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TextField
              type="date"
              size="small"
              value={transactionDate}
              onChange={(e) => {
                const newDate = e.target.value;
                setTransactionDate(newDate);
                if (posActiveTab === 'return') {
                  const updatedItems = returnItems.map(item => {
                    if (item.issueDate) {
                      const newDays = calculateDays(item.issueDate, newDate);
                      const amount = (parseFloat(item.returnQty) || 0) * (parseFloat(item.rent) || 0) * newDays;
                      return { ...item, days: newDays, amount: amount };
                    }
                    return item;
                  });
                  setReturnItems(updatedItems);
                }
              }}
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                borderRadius: 1,
                width: 150,
                '& .MuiOutlinedInput-input': { color: 'white', py: 0.8 }
              }}
            />
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => setPosOpen(false)}
              sx={{ borderColor: 'rgba(255,255,255,0.5)', fontWeight: 'bold' }}
            >
              Exit POS (X)
            </Button>
          </Box>
        </Box>

        {/* POS MAIN SPLIT VIEW IN 3 VERTICAL COLUMNS */}
        <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          
          {/* COLUMN 1 - CATALOG PANEL */}
          <Box sx={{
            flex: 5,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            bgcolor: '#f8fafc',
            borderRight: '1px solid #cbd5e1'
          }}>
            {/* Column Header Button: Material Issue Tab */}
            <Button
              fullWidth
              onClick={() => setPosActiveTab('issue')}
              sx={{
                py: 2,
                borderRadius: 0,
                fontWeight: 'bold',
                fontSize: '1.05rem',
                color: posActiveTab === 'issue' ? 'white' : '#475569',
                bgcolor: posActiveTab === 'issue' ? '#2e7d32' : '#f1f5f9',
                borderBottom: posActiveTab === 'issue' ? '3px solid #1b5e20' : '1px solid #cbd5e1',
                '&:hover': { bgcolor: posActiveTab === 'issue' ? '#1b5e20' : '#e2e8f0' }
              }}
            >
              🚚 साहित्य देणे (MATERIAL ISSUE)
            </Button>

            {/* Column Body: Material Catalog / Pending Grid */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
              {posActiveTab === 'issue' ? (
                <>
                  {/* Search Filter */}
                  <Box sx={{ mb: 2 }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="साहित्य शोधा... (उदा. प्लेट, पाईप)"
                      value={posSearchQuery}
                      onChange={(e) => setPosSearchQuery(e.target.value)}
                      sx={{
                        bgcolor: 'white',
                        borderRadius: 2,
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { borderRadius: '8px' }
                        }
                      }}
                    />
                  </Box>

                  {/* Grid of Materials */}
                  <Grid container spacing={2} sx={{ pb: 3 }}>
                    {materials
                      .filter(m => m.name.toLowerCase().includes(posSearchQuery.toLowerCase()))
                      .map(m => {
                        const isLowStock = m.available_stock < 20;
                        let icon = '🧱';
                        if (m.name.includes('पाईप') || m.name.includes('Pipe')) icon = '🦯';
                        else if (m.name.includes('जॅक') || m.name.includes('Jack')) icon = '🗼';
                        else if (m.name.includes('फळी') || m.name.includes('Plank') || m.name.includes('लाकडी')) icon = '🪵';
                        else if (m.name.includes('शिकंजा') || m.name.includes('Clamp')) icon = '🗜️';
                        else if (m.name.includes('कप्पा') || m.name.includes('Coupler')) icon = '🔗';

                        return (
                          <Grid item xs={12} sm={6} md={4} key={m.id}>
                            <Paper
                              elevation={1}
                              onClick={() => handlePosAddToCart(m)}
                              sx={{
                                p: 1.5,
                                borderRadius: 3,
                                border: '1px solid #e2e8f0',
                                cursor: 'pointer',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                height: 160,
                                position: 'relative',
                                transition: 'transform 0.15s, box-shadow 0.15s',
                                '&:hover': {
                                  transform: 'translateY(-3px)',
                                  boxShadow: '0 8px 20px rgba(0,0,0,0.08)',
                                  borderColor: '#2e7d32'
                                },
                                '&:active': { transform: 'scale(0.97)' }
                              }}
                            >
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography sx={{ fontSize: '1.8rem', mb: 0.5 }}>{icon}</Typography>
                                <Typography sx={{ fontWeight: 'bold', fontSize: '0.85rem', lineHeight: 1.2, mb: 0.5, color: '#1e293b' }}>
                                  {m.name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    bgcolor: isLowStock ? '#fee2e2' : '#f1f5f9',
                                    color: isLowStock ? '#ef4444' : '#64748b',
                                    px: 1,
                                    py: 0.2,
                                    borderRadius: 1,
                                    fontWeight: 'bold',
                                    fontSize: '0.75rem'
                                  }}
                                >
                                  {isLowStock ? '⚠️ कमी: ' : 'स्टॉक: '}{m.available_stock} नग
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px dashed #e2e8f0', pt: 0.5, mt: 0.5 }}>
                                <Typography sx={{ fontWeight: 'bold', color: '#1b5e20', fontSize: '0.8rem' }}>
                                  ₹{m.rate_per_day}/दिवस
                                </Typography>
                                <IconButton
                                  size="small"
                                  sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', '&:hover': { bgcolor: '#2e7d32', color: 'white' } }}
                                  onClick={(e) => { e.stopPropagation(); handlePosAddToCart(m); }}
                                >
                                  <AddCircleOutlineIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Paper>
                          </Grid>
                        );
                      })}
                  </Grid>
                </>
              ) : (
                <>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5, color: '#e65100', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    ग्राहकाकडून येणे असणारे साहित्य (चलान नुसार)
                  </Typography>
                  {posPendingLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress color="warning" /></Box>
                  ) : posPendingBillItems.length === 0 ? (
                    <Typography sx={{ color: '#64748b', p: 3, textAlign: 'center', fontSize: '0.9rem' }}>
                      ग्राहकाकडून कोणतेही साहित्य येणे नाही.
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {(() => {
                        const pendingChallans = {};
                        posPendingBillItems.forEach(item => {
                          const cId = item.challan_id;
                          if (!pendingChallans[cId]) {
                            pendingChallans[cId] = {
                              challan_id: cId,
                              issue_date: item.issue_date,
                              items: []
                            };
                          }
                          pendingChallans[cId].items.push(item);
                        });
                        const challansList = Object.values(pendingChallans);

                        return challansList.map((ch, cIdx) => {
                          const isSelected = posSelectedChallanId === ch.challan_id;
                          return (
                            <Paper
                              key={cIdx}
                              elevation={isSelected ? 2 : 1}
                              onClick={() => handleSelectChallanForReturn(ch)}
                              sx={{
                                p: 1.5,
                                borderRadius: 3,
                                border: '2px solid',
                                borderColor: isSelected ? '#e65100' : '#e2e8f0',
                                bgcolor: isSelected ? '#fff8f1' : 'white',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': {
                                  borderColor: '#e65100',
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                                }
                              }}
                            >
                              <Grid container spacing={1.5} alignItems="center">
                                <Grid item xs={5} sx={{ borderRight: '1px dashed #cbd5e1', pr: 1 }}>
                                  <Typography sx={{ fontWeight: 'bold', fontSize: '0.85rem', color: '#e65100' }}>
                                    चलन क्र. #{ch.challan_id}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.72rem', display: 'block', mt: 0.5 }}>
                                    📅 {formatDate(ch.issue_date)}
                                  </Typography>
                                </Grid>
                                <Grid item xs={7} sx={{ pl: 1 }}>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    {ch.items.map((item, iIdx) => (
                                      <Box key={iIdx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#334155', fontWeight: '500' }}>
                                          • {item.material_name}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#d32f2f' }}>
                                          {item.pending_qty} नग
                                        </Typography>
                                      </Box>
                                    ))}
                                  </Box>
                                </Grid>
                              </Grid>
                            </Paper>
                          );
                        });
                      })()}
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Box>

          {/* COLUMN 2 - CART ITEMS PANEL */}
          <Box sx={{
            flex: 4.5,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            bgcolor: 'white',
            borderRight: '1px solid #cbd5e1'
          }}>
            {/* Column Header Button: Material Return Tab */}
            <Button
              fullWidth
              onClick={() => {
                setPosActiveTab('return');
                if (customer) {
                  fetchPosPendingItems(customer.id);
                }
              }}
              sx={{
                py: 2,
                borderRadius: 0,
                fontWeight: 'bold',
                fontSize: '1.05rem',
                color: posActiveTab === 'return' ? 'white' : '#475569',
                bgcolor: posActiveTab === 'return' ? '#e65100' : '#f1f5f9',
                borderBottom: posActiveTab === 'return' ? '3px solid #b53d00' : '1px solid #cbd5e1',
                '&:hover': { bgcolor: posActiveTab === 'return' ? '#b53d00' : '#e2e8f0' }
              }}
            >
              🔄 साहित्य घेणे / जमा (MATERIAL RETURN)
            </Button>

            {/* Inner Cart Header */}
            <Box sx={{ p: 1.5, borderBottom: '1px solid #cbd5e1', display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#fafbfb' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: posActiveTab === 'issue' ? '#2e7d32' : '#e65100', display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.9rem' }}>
                🛍️ साहित्य यादी (Cart Items)
              </Typography>
              <Button
                size="small"
                color="error"
                onClick={posActiveTab === 'issue' ? handlePosClearCart : () => setReturnItems([])}
                sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 0.2 }}
              >
                सर्व काढा
              </Button>
            </Box>

            {/* Scrollable Cart Items List */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {posActiveTab === 'issue' ? (
                cartItems.filter(item => item.material).length === 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#94a3b8', py: 4 }}>
                    <Typography variant="h3" sx={{ mb: 0.5, opacity: 0.5 }}>📦</Typography>
                    <Typography sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>कार्ट रिकामे आहे!</Typography>
                    <Typography variant="caption">डावीकडील साहित्यावर क्लिक करून नग जोडा.</Typography>
                  </Box>
                ) : (
                  cartItems
                    .filter(item => item.material)
                    .map((item, idx) => {
                      const itemTotal = (parseFloat(item.qty) || 0) * (parseFloat(item.material.rate_per_day) || 0);
                      return (
                        <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f8fafc', p: 1, borderRadius: 2, border: '1px solid #e2e8f0' }}>
                          <Box sx={{ flex: 1, pr: 1 }}>
                            <Typography sx={{ fontWeight: 'bold', fontSize: '0.85rem', color: '#334155' }}>{item.material.name}</Typography>
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                              दर: ₹{item.material.rate_per_day}/दिवस | भाडे: ₹{itemTotal}/दिवस
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <IconButton
                              size="small"
                              onClick={() => handlePosUpdateQty(idx, -1)}
                              sx={{ border: '1px solid #cbd5e1', borderRadius: 1, p: 0.2, width: 22, height: 22 }}
                            >
                              <Typography sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>-</Typography>
                            </IconButton>
                            <TextField
                              size="small"
                              type="number"
                              value={item.qty}
                              onChange={(e) => handlePosChangeQtyDirect(idx, e.target.value)}
                              inputProps={{ style: { textAlign: 'center', fontWeight: 'bold', padding: '2px', fontSize: '0.8rem' } }}
                              sx={{
                                width: 50,
                                '& .MuiOutlinedInput-input': { p: '2px 4px', textAlign: 'center' }
                              }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => handlePosUpdateQty(idx, 1)}
                              sx={{ border: '1px solid #cbd5e1', borderRadius: 1, p: 0.2, width: 22, height: 22 }}
                            >
                              <Typography sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>+</Typography>
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => handlePosDeleteCartItem(idx)} sx={{ ml: 0.5, p: 0.2 }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      );
                    })
                )
              ) : (
                returnItems.length === 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#94a3b8', py: 4 }}>
                    <Typography variant="h3" sx={{ mb: 0.5, opacity: 0.5 }}>🔄</Typography>
                    <Typography sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>कोणतेही साहित्य निवडले नाही!</Typography>
                    <Typography variant="caption">डावीकडील येणे असणारे साहित्यवर क्लिक करून जमा करा.</Typography>
                  </Box>
                ) : (
                  returnItems.map((item, idx) => (
                    <Box key={idx} sx={{ display: 'flex', flexDirection: 'column', bgcolor: '#fff8f1', p: 1, borderRadius: 2, border: '1px solid #ffe0b2', gap: 0.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontWeight: 'bold', fontSize: '0.85rem', color: '#451a03' }}>{item.material_name}</Typography>
                        <IconButton size="small" color="error" onClick={() => setReturnItems(returnItems.filter((_, i) => i !== idx))} sx={{ p: 0.2 }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                          दर: ₹{item.rent} | पेंडिंग: {item.pending}
                        </Typography>
                        <Typography sx={{ fontWeight: 'bold', color: '#e65100', fontSize: '0.85rem' }}>
                          भाडे: ₹{item.amount?.toFixed(0)}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5, alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>परत नग:</Typography>
                          <IconButton
                            size="small"
                            onClick={() => handlePosUpdateReturnQty(idx, -1)}
                            sx={{ border: '1px solid #cbd5e1', borderRadius: 1, p: 0.1, width: 20, height: 20 }}
                          >
                            -
                          </IconButton>
                          <TextField
                            size="small"
                            type="number"
                            value={item.returnQty}
                            onChange={(e) => handlePosChangeReturnQtyDirect(idx, e.target.value)}
                            inputProps={{ style: { textAlign: 'center', fontWeight: 'bold', padding: '2px', fontSize: '0.8rem' } }}
                            sx={{
                              width: 50,
                              '& .MuiOutlinedInput-input': { p: '2px 4px', textAlign: 'center' }
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => handlePosUpdateReturnQty(idx, 1)}
                            sx={{ border: '1px solid #cbd5e1', borderRadius: 1, p: 0.1, width: 20, height: 20 }}
                          >
                            +
                          </IconButton>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>दर:</Typography>
                          <TextField
                            size="small"
                            type="number"
                            value={item.rent}
                            onChange={(e) => handlePosUpdateReturnRent(idx, e.target.value)}
                            inputProps={{ style: { textAlign: 'center', fontWeight: 'bold', padding: '2px', fontSize: '0.8rem' } }}
                            sx={{
                              width: 50,
                              '& .MuiOutlinedInput-input': { p: '2px 4px', textAlign: 'center' }
                            }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>दिवस:</Typography>
                          <TextField
                            size="small"
                            type="number"
                            value={item.days}
                            onChange={(e) => handlePosUpdateReturnDays(idx, e.target.value)}
                            inputProps={{ style: { textAlign: 'center', fontWeight: 'bold', padding: '2px', fontSize: '0.8rem' } }}
                            sx={{
                              width: 45,
                              '& .MuiOutlinedInput-input': { p: '2px 4px', textAlign: 'center' }
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  ))
                )
              )}
            </Box>

            {/* Footer Box (Total Estimated Summary Block) inside Middle Column */}
            <Box sx={{
              p: 1.5,
              bgcolor: posActiveTab === 'issue' ? '#e8f5e9' : '#fff3e0',
              borderTop: `2px solid ${posActiveTab === 'issue' ? '#2e7d32' : '#e65100'}`
            }}>
              {posActiveTab === 'issue' ? (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.2 }}>
                    <Typography variant="caption" color="textSecondary">एकूण साहित्य प्रकार:</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>{cartItems.filter(item => item.material).length}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" color="textSecondary">एकूण नग संख्या:</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                      {cartItems.filter(item => item.material).reduce((sum, item) => sum + (parseFloat(item.qty) || 0), 0)} नग
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px dashed rgba(46, 125, 50, 0.3)', pt: 0.5, alignItems: 'center' }}>
                    <Typography sx={{ fontWeight: 'bold', fontSize: '0.9rem', color: '#1b5e20' }}>एकूण भाडे दर:</Typography>
                    <Typography sx={{ fontWeight: 'bold', fontSize: '1.15rem', color: '#1b5e20' }}>
                      ₹ {cartItems.filter(item => item.material).reduce((sum, item) => sum + ((parseFloat(item.qty) || 0) * (parseFloat(item.material.rate_per_day) || 0)), 0)} / दिवस
                    </Typography>
                  </Box>
                </>
              ) : (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.2 }}>
                    <Typography variant="caption" color="textSecondary">एकूण रिटर्न साहित्य:</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>{returnItems.length}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.2 }}>
                    <Typography variant="caption" color="textSecondary">एकूण भाडे (Rent):</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                      ₹ {returnItems.reduce((sum, item) => sum + (item.amount || 0), 0).toFixed(0)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" color="textSecondary">इतर आकार (Other Charges):</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                      ₹ {totalOtherChargesAmount.toFixed(0)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px dashed rgba(230, 81, 0, 0.3)', pt: 0.5, alignItems: 'center' }}>
                    <Typography sx={{ fontWeight: 'bold', fontSize: '0.9rem', color: '#b53d00' }}>एकूण नावे रक्कम:</Typography>
                    <Typography sx={{ fontWeight: 'bold', fontSize: '1.15rem', color: '#b53d00' }}>
                      ₹ {(returnItems.reduce((sum, item) => sum + (item.amount || 0), 0) + totalOtherChargesAmount).toFixed(0)}
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          </Box>

          {/* COLUMN 3 - METADATA & ACTION PANEL */}
          <Box sx={{
            flex: 3.5,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            bgcolor: 'white',
            p: 2
          }}>
            {/* Body Content - Metadata Input Fields */}
            <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
              {posActiveTab === 'issue' ? (
                <>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#475569', borderBottom: '1px solid #cbd5e1', pb: 0.5 }}>
                    📋 चलन माहिती (Details)
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="साइट लोकेशन"
                        value={siteLocation}
                        onChange={(e) => setSiteLocation(e.target.value)}
                        InputProps={{ startAdornment: <InputAdornment position="start">📍</InputAdornment> }}
                        sx={{
                          bgcolor: 'white',
                          '& .MuiOutlinedInput-root': { borderRadius: '6px', fontSize: '0.8rem' }
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="गाडी नंबर"
                        value={vehicleNo}
                        onChange={(e) => setVehicleNo(e.target.value)}
                        InputProps={{ startAdornment: <InputAdornment position="start">🚚</InputAdornment> }}
                        sx={{
                          bgcolor: 'white',
                          '& .MuiOutlinedInput-root': { borderRadius: '6px', fontSize: '0.8rem' }
                        }}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="पावती नं."
                        value={receiptNo}
                        onChange={(e) => setReceiptNo(e.target.value)}
                        InputProps={{ startAdornment: <InputAdornment position="start">🧾</InputAdornment> }}
                        sx={{
                          bgcolor: 'white',
                          '& .MuiOutlinedInput-root': { borderRadius: '6px', fontSize: '0.8rem' }
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="रेफरन्स"
                        value={reference}
                        onChange={(e) => setReference(e.target.value)}
                        InputProps={{ startAdornment: <InputAdornment position="start">👤</InputAdornment> }}
                        sx={{
                          bgcolor: 'white',
                          '& .MuiOutlinedInput-root': { borderRadius: '6px', fontSize: '0.8rem' }
                        }}
                      />
                    </Grid>
                  </Grid>
                </>
              ) : (
                <>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#475569', borderBottom: '1px solid #cbd5e1', pb: 0.5 }}>
                    📋 जमा नोंद माहिती (Return Details)
                  </Typography>
                  <TextField
                    fullWidth
                    label="रिमार्क / टीप (Remark)"
                    size="small"
                    multiline
                    rows={4}
                    placeholder="उदा. काही साहित्य डॅमेज आहे किंवा इतर नोंद..."
                    value={paymentData.remark}
                    onChange={(e) => setPaymentData({ ...paymentData, remark: e.target.value })}
                    sx={{
                      bgcolor: 'white',
                      '& .MuiOutlinedInput-root': { borderRadius: '6px' }
                    }}
                  />

                  <Box sx={{ mt: 2, borderTop: '1px solid #e2e8f0', pt: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#e65100', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>इतर आकार (Other Charges)</span>
                      <Button size="small" variant="outlined" color="warning" onClick={handleAddOtherCharge} sx={{ py: 0.2, fontSize: '0.75rem' }}>
                        + चार्ज जोडा
                      </Button>
                    </Typography>

                    {otherCharges.map((charge, idx) => (
                      <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                        <TextField
                          size="small"
                          placeholder="उदा. वाहतूक भाडे"
                          value={charge.name}
                          onChange={(e) => handleUpdateOtherCharge(idx, 'name', e.target.value)}
                          fullWidth
                          sx={{ bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: '6px', fontSize: '0.8rem' } }}
                        />
                        <TextField
                          size="small"
                          placeholder="रक्कम"
                          type="number"
                          value={charge.amount}
                          onChange={(e) => handleUpdateOtherCharge(idx, 'amount', e.target.value)}
                          sx={{ width: 100, bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: '6px', fontSize: '0.8rem' } }}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                          }}
                        />
                        <IconButton color="error" size="small" onClick={() => handleRemoveOtherCharge(idx)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </Box>

            {/* Action Footer */}
            <Box sx={{ borderTop: '1px solid #e2e8f0', pt: 1.5, mt: 'auto' }}>
              {error && <Alert severity="error" sx={{ mt: 0, mb: 1, py: 0.2 }}>{error}</Alert>}

              <Button
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                onClick={() => handleSave(posActiveTab)}
                sx={{
                  py: 1.5,
                  fontWeight: 'bold',
                  fontSize: '1.1rem',
                  bgcolor: posActiveTab === 'issue' ? '#2e7d32' : '#e65100',
                  boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                  '&:hover': { bgcolor: posActiveTab === 'issue' ? '#1b5e20' : '#b53d00' }
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : '✅ व्यवहार पूर्ण करा (SAVE)'}
              </Button>

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
                <FormControlLabel
                  control={<Checkbox checked={sendWhatsApp} onChange={(e) => setSendWhatsApp(e.target.checked)} color="success" size="small" />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <WhatsAppIcon sx={{ color: '#25D366', fontSize: '1rem' }} />
                      <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#475569' }}>
                        WhatsApp द्वारे पावती पाठवा
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Dialog>

      {/* WhatsApp Share / Success Dialog */}
      <Dialog
        open={Boolean(whatsappShareData)}
        onClose={() => setWhatsappShareData(null)}
        PaperProps={{
          sx: { borderRadius: 4, p: 1, maxWidth: 380, width: '90%' }
        }}
      >
        <DialogContent sx={{ textAlign: 'center', py: 2.5 }}>
          <Box sx={{ fontSize: '3rem', mb: 1 }}>
            🎉
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: '#2e7d32' }}>
            व्यवहार यशस्वीरित्या जतन केला!
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mb: 3, px: 1 }}>
            ग्राहक <strong>{whatsappShareData?.customerName}</strong> यांच्यासाठी <strong>{whatsappShareData?.type} #{whatsappShareData?.challanId}</strong> यशस्वीरित्या तयार झाले आहे.
          </Typography>
          
          <Button
            fullWidth
            variant="contained"
            color="success"
            size="large"
            startIcon={<WhatsAppIcon />}
            onClick={() => {
              if (whatsappShareData?.url) {
                window.open(whatsappShareData.url, '_blank');
              }
              setWhatsappShareData(null);
            }}
            sx={{
              py: 1.5,
              fontWeight: 'bold',
              borderRadius: 3,
              fontSize: '1rem',
              bgcolor: '#25D366',
              '&:hover': { bgcolor: '#128C7E' },
              boxShadow: '0 4px 12px rgba(37,211,102,0.3)'
            }}
          >
            WhatsApp वर पावती पाठवा
          </Button>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', borderTop: '1px solid #f1f5f9', pt: 1.5, pb: 1 }}>
          <Button 
            onClick={() => setWhatsappShareData(null)} 
            sx={{ color: '#94a3b8', fontWeight: 'bold' }}
          >
            फक्त बंद करा
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerTransaction;
